import clsx from 'clsx'
import { ModerationBoundaryView, useDeleteModerationBoundaryMutation } from '../../api/client'
import { ModerationBoundaryTemplate } from '../../models/Moderation'
import { toastError, toastSuccess } from '../Notification'
import SimpleLoader from '../SimpleLoader'
import { PROMPT_MANAGED_MESSAGE } from './promptManaged'

type Props = {
    boundary: ModerationBoundaryView
    onEditClick: (boundary: ModerationBoundaryView) => void
    showCompare: boolean
    compareWith?: ModerationBoundaryTemplate
    onDelete: (moderationBoundaryView: ModerationBoundaryView) => void
    /**
     * True when this boundary's classifier has a live prompt. Deployment writes
     * these rows from the live thresholds and re-points them at every go-live, so
     * they are shown but not editable here.
     */
    promptManaged: boolean
}
function ModerationBoundaryRow(props: Props) {
    const { boundary, onEditClick, showCompare, compareWith, onDelete, promptManaged } = props
    const [deleteModerationBoundary, { loading: deleteModerationBoundaryLoading }] =
        useDeleteModerationBoundaryMutation({
            onError: () => {
                toastError('Failed to delete moderation boundary')
            },
            onCompleted: () => {
                toastSuccess('Moderation boundary deleted')
            }
        })

    const sameBgClassName = 'bg-green-100'
    const differentBgClassName = 'bg-amber-100'

    let compareWithClassName = []
    if (showCompare) {
        if (compareWith?.probability && boundary.probability) {
            if (compareWith?.probability === boundary.probability) {
                compareWithClassName.push(sameBgClassName)
            } else {
                compareWithClassName.push(differentBgClassName)
            }
        }

        if (!!!compareWith) {
            // It is not defined in the template - this field will be deleted
            compareWithClassName.push(differentBgClassName)
        }
    }
    const compareWithDisplay = compareWith ? (
        <div className={compareWithClassName.join(' ')}>
            {compareWith.probability}
        </div>
    ) : null

    return (
        <tr key={boundary.id} className={compareWithClassName.join(' ')}>
            <td className="px-3 py-2.5">
                <div className="text-sm text-ink-900">{boundary.classifier.displayName}</div>
                <div className="text-xs text-gray-600">
                    {boundary.classifier.shortName} · #{boundary.classifier.id}
                </div>
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 text-right text-sm tabular-nums text-ink-900">
                {boundary.probability}
            </td>
            {showCompare && (
                <td className="whitespace-nowrap px-3 py-2.5 text-right text-sm tabular-nums text-ink-900">
                    {compareWithDisplay}
                </td>
            )}
            <td className="whitespace-nowrap px-3 py-2.5 text-right text-sm">
                <div
                    className={clsx('flex flex-row justify-end', promptManaged && 'cursor-not-allowed')}
                    data-tooltip-id={promptManaged ? 'tooltip-id' : undefined}
                    data-tooltip-content={promptManaged ? PROMPT_MANAGED_MESSAGE : undefined}
                >
                    <button
                        onClick={() => {
                            onEditClick(boundary)
                        }}
                        disabled={promptManaged}
                        className={clsx(
                            'mr-3',
                            promptManaged
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-primary hover:text-primary-900'
                        )}
                    >
                        Edit
                    </button>
                    {!deleteModerationBoundaryLoading && (
                        <button
                            onClick={async () => {
                                await deleteModerationBoundary({
                                    variables: {
                                        moderationBoundaryId: boundary.id
                                    }
                                })
                                onDelete(boundary)
                            }}
                            className={clsx(
                                promptManaged
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-severe hover:text-primary-900'
                            )}
                            disabled={promptManaged || deleteModerationBoundaryLoading}
                        >
                            Delete
                        </button>
                    )}
                    <SimpleLoader loading={deleteModerationBoundaryLoading} />
                </div>
            </td>
        </tr>
    )
}

export default ModerationBoundaryRow
